import React, { useState } from 'react';

interface MethodParameter {
  name: string;
  desc: string;
}

interface MethodCardProps {
  engine: 'SciPy' | 'RDKit' | 'Vina' | 'PySCF' | 'PaDEL' | 'OpenBabel' | 'OPSIN' | 'STOUT' | 'SymPy' | 'mrgsolve' | 'ELN Hub' | 'Metabolism Hub' | 'Genomics Hub' | 'GenBio Hub';
  title: string;
  description: string;
  equation?: string;
  parameters?: MethodParameter[];
  reference?: string;
  referenceUrl?: string;
  source?: string;
}

const engineColors: Record<string, string> = {
  SciPy: '#3B82F6',
  RDKit: '#10B981',
  Vina: '#F59E0B',
  PySCF: '#EF4444',
  PaDEL: '#8B5CF6',
  OpenBabel: '#06B6D4',
  OPSIN: '#EC4899',
  STOUT: '#F97316',
  'ELN Hub': '#0D9488',
  'Metabolism Hub': '#8B5CF6',
  'Genomics Hub': '#06B6D4',
  'GenBio Hub': '#EC4899',
};

const MethodCard: React.FC<MethodCardProps> = ({
  engine,
  title,
  description,
  equation,
  parameters,
  reference,
  referenceUrl,
  source,
}) => {
  const [expanded, setExpanded] = useState(false);
  const badgeColor = engineColors[engine] || '#64748B';

  return (
    <div
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 12,
        overflow: 'hidden',
        transition: 'all 250ms ease',
      }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--text-primary)',
          fontSize: 13,
          textAlign: 'left',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            fontFamily: 'monospace',
            background: `${badgeColor}22`,
            color: badgeColor,
            border: `1px solid ${badgeColor}44`,
            whiteSpace: 'nowrap',
          }}
        >
          {engine}
        </span>
        <span style={{ flex: 1, fontWeight: 500 }}>{title}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          style={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease',
          }}
        >
          <path
            d="M3.5 5.25L7 8.75L10.5 5.25"
            stroke="var(--text-muted)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {expanded && (
        <div style={{ padding: '0 14px 14px', fontSize: 13 }}>
          <p style={{ color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>{description}</p>

          {equation && (
            <div
              style={{
                background: 'var(--hover-bg)',
                borderRadius: 8,
                padding: '10px 14px',
                marginBottom: 10,
                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                fontSize: 13,
                color: '#F59E0B',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              {equation}
            </div>
          )}

          {parameters && parameters.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ color: '#a7abb6', fontSize: 11, fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Parameters
              </div>
              {parameters.map((p, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 2 }}>
                  <span style={{ color: '#F59E0B', fontFamily: 'monospace', minWidth: 80 }}>{p.name}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{p.desc}</span>
                </div>
              ))}
            </div>
          )}

          {reference && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
              <span style={{ fontWeight: 500 }}>Ref: </span>
              {referenceUrl ? (
                <a
                  href={referenceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#7dd3fc', textDecoration: 'none' }}
                >
                  {reference}
                </a>
              ) : (
                reference
              )}
            </div>
          )}

          {source && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>
              <span style={{ fontWeight: 500 }}>Engine: </span>
              <span style={{ fontFamily: 'monospace' }}>{source}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MethodCard;
